/**
 * RedemptionAnalytics
 *
 * Business owner view of token redemptions at their listing. Shows:
 *   - Summary cards (confirmed, pending, tokens redeemed, unique members)
 *   - Daily redemption trend chart
 *   - Item popularity breakdown
 *   - Method split (self-service vs staff-initiated)
 *   - Recent redemption history table
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins,
  faUsers,
  faClock,
  faCheckCircle,
  faGift,
  faSync,
  faChartBar,
  faHistory,
  faMobileAlt,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '../FirebaseConfig';
import { Skeleton, EmptyState } from './ui';
import { formatShortDate } from '../utils/dateUtils';
import '../styles/RedemptionAnalytics.css';

// ─── helpers ──────────────────────────────────────────────────────────────────

const getDateRangeStart = (dateRange) => {
  const now = new Date();
  switch (dateRange) {
    case '7days':  return new Date(now.getTime() - 7   * 86400000);
    case '30days': return new Date(now.getTime() - 30  * 86400000);
    case '90days': return new Date(now.getTime() - 90  * 86400000);
    case 'year':   return new Date(now.getTime() - 365 * 86400000);
    default:       return new Date(now.getTime() - 30  * 86400000);
  }
};

const isStaffInitiatedRedemption = (row) => {
  const method = (row?.method || '').toLowerCase();
  return (
    method === 'staff_scan'
    || method === 'staff_initiated'
    || method === 'staff'
    || !!row?.confirmedByStaffNumber
    || !!row?.staffAccountId
  );
};

// ─── sub-components ───────────────────────────────────────────────────────────

const SummaryCard = ({ title, value, subtitle, icon, colorClass, loading }) => (
  <div className={`ra-card ${loading ? 'ra-card--loading' : ''}`}>
    <div className={`ra-card__icon ${colorClass}`}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="ra-card__body">
      <span className="ra-card__title">{title}</span>
      <span className="ra-card__value">{loading ? '—' : value}</span>
      {subtitle && <span className="ra-card__subtitle">{loading ? '' : subtitle}</span>}
    </div>
  </div>
);

const DailyTrendChart = ({ data, loading }) => {
  if (loading) return <div className="ra-chart-placeholder">Loading chart…</div>;
  if (!data || data.length === 0) return <div className="ra-chart-placeholder">No redemptions in this period.</div>;

  const maxVal = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="ra-daily-chart">
      <div className="ra-bars">
        {data.map((day, i) => {
          const pct = (day.count / maxVal) * 100;
          return (
            <div key={i} className="ra-bar-col">
              <div className="ra-bar-track">
                <div
                  className="ra-bar-fill"
                  style={{ height: `${pct}%` }}
                  title={`${day.label}: ${day.count} redemption${day.count !== 1 ? 's' : ''} · ${day.tokens} tokens`}
                />
              </div>
              {i % Math.max(1, Math.floor(data.length / 8)) === 0 && (
                <span className="ra-bar-label">{day.label}</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="ra-chart-legend">
        <span className="ra-legend-dot" /> Confirmed Redemptions
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const RedemptionAnalytics = ({ businessId }) => {
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [dateRange, setDateRange]     = useState('30days');
  const [summary, setSummary]         = useState(null);
  const [dailyData, setDailyData]     = useState([]);
  const [itemBreakdown, setItemBreakdown] = useState([]);
  const [recentRows, setRecentRows]   = useState([]);
  const [methodSplit, setMethodSplit] = useState(null);

  const loadData = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);

    try {
      const startTs = Timestamp.fromDate(getDateRangeStart(dateRange));

      // 1. Fetch all redemptions for this business in date range
      const rdSnap = await getDocs(query(
        collection(firestore, 'redemptions'),
        where('businessId', '==', businessId),
        where('createdAt', '>=', startTs),
        orderBy('createdAt', 'desc'),
        limit(500),
      ));

      const redemptions = [];
      rdSnap.forEach(doc => redemptions.push({ id: doc.id, ...doc.data() }));

      // 2. Load redeemable items for name lookup
      const itemsSnap = await getDocs(
        collection(firestore, 'businessListings', businessId, 'redeemableItems')
      );
      const itemMap = {};
      itemsSnap.forEach(doc => {
        itemMap[doc.id] = doc.data().name || 'Unknown Item';
      });

      // 3. Summary metrics
      let confirmed = 0, pending = 0, totalTokens = 0;
      const uniqueMembers = new Set();
      let selfService = 0, staffInitiated = 0;

      for (const r of redemptions) {
        if (r.status === 'confirmed') {
          confirmed++;
          totalTokens += r.tokenCost || 0;
        } else if (r.status === 'pending') {
          pending++;
        }
        if (r.userId) uniqueMembers.add(r.userId);
        if (isStaffInitiatedRedemption(r)) staffInitiated++;
        else selfService++;
      }

      const avgTokensPerRedemption = confirmed > 0
        ? Math.round(totalTokens / confirmed)
        : 0;

      setSummary({
        confirmed,
        pending,
        totalTokens,
        uniqueMembers: uniqueMembers.size,
        avgTokensPerRedemption,
        total: redemptions.length,
      });

      // 4. Method split
      setMethodSplit({
        selfService,
        staffInitiated,
        selfServicePct: redemptions.length > 0
          ? Math.round((selfService / redemptions.length) * 100)
          : 0,
        staffPct: redemptions.length > 0
          ? Math.round((staffInitiated / redemptions.length) * 100)
          : 0,
      });

      // 5. Daily trend (confirmed only)
      const dailyMap = {};
      for (const r of redemptions) {
        if (r.status !== 'confirmed') continue;
        const date = r.createdAt?.toDate?.() || new Date();
        const key  = date.toISOString().slice(0, 10);
        if (!dailyMap[key]) dailyMap[key] = { count: 0, tokens: 0 };
        dailyMap[key].count++;
        dailyMap[key].tokens += r.tokenCost || 0;
      }

      const dailySorted = Object.entries(dailyMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateStr, vals]) => {
          const d = new Date(dateStr + 'T12:00:00');
          return {
            date: dateStr,
            label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            ...vals,
          };
        });
      setDailyData(dailySorted);

      // 6. Item popularity (confirmed only)
      const itemAgg = {};
      for (const r of redemptions) {
        if (r.status !== 'confirmed') continue;
        const iid = r.redeemableItemId || 'unknown';
        if (!itemAgg[iid]) {
          itemAgg[iid] = {
            id: iid,
            name: itemMap[iid] || r.itemName || 'Unknown Item',
            count: 0,
            tokens: 0,
          };
        }
        itemAgg[iid].count++;
        itemAgg[iid].tokens += r.tokenCost || 0;
      }

      const itemList = Object.values(itemAgg)
        .sort((a, b) => b.count - a.count)
        .map(item => ({
          ...item,
          pct: confirmed > 0 ? Math.round((item.count / confirmed) * 100) : 0,
        }));
      setItemBreakdown(itemList);

      // 7. Recent redemptions (all statuses, sorted newest first)
      const recent = redemptions.slice(0, 20).map(r => ({
        id: r.id,
        date: r.createdAt?.toDate?.() || new Date(),
        itemName: itemMap[r.redeemableItemId] || r.itemName || 'Unknown Item',
        status: r.status,
        tokenCost: r.tokenCost || 0,
        method: r.method || 'self_service',
        staffAccountId: r.staffAccountId || null,
        confirmedByStaffNumber: r.confirmedByStaffNumber || null,
        validationCode: r.validationCode || '',
      }));
      setRecentRows(recent);

    } catch (err) {
      console.error('[RedemptionAnalytics] load error:', err);
      setError('Failed to load redemption data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [businessId, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── render helpers ───────────────────────────────────────────────────────────

  const statusLabel = (status) => {
    switch (status) {
      case 'confirmed': return <span className="ra-status ra-status--confirmed">Confirmed</span>;
      case 'pending':   return <span className="ra-status ra-status--pending">Pending</span>;
      case 'cancelled': return <span className="ra-status ra-status--cancelled">Cancelled</span>;
      case 'expired':   return <span className="ra-status ra-status--expired">Expired</span>;
      default:          return <span className="ra-status">{status}</span>;
    }
  };

  const methodLabel = (row) => {
    if (isStaffInitiatedRedemption(row)) {
      return (
        <span className="ra-method ra-method--staff">
          <FontAwesomeIcon icon={faUserShield} /> Staff
        </span>
      );
    }
    return (
      <span className="ra-method ra-method--self">
        <FontAwesomeIcon icon={faMobileAlt} /> Self-service
      </span>
    );
  };

  if (!businessId) {
    return <EmptyState message="Select a listing to view redemption analytics." />;
  }

  return (
    <div className="redemption-analytics">
      {/* Header */}
      <div className="ra-header">
        <div className="ra-header__title">
          <h2>
            <FontAwesomeIcon icon={faGift} />
            Redemption Analytics
          </h2>
          <p>Track token redemptions at your business — item popularity, trends, and member engagement.</p>
        </div>
        <div className="ra-header__controls">
          <select
            className="ra-select"
            value={dateRange}
            onChange={e => setDateRange(e.target.value)}
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
            <option value="year">Last 12 months</option>
          </select>
          <button
            className="ra-refresh-btn"
            onClick={loadData}
            disabled={loading}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="ra-error">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      )}

      {/* Summary cards */}
      <div className="ra-cards-grid">
        <SummaryCard
          title="Confirmed Redemptions"
          value={summary?.confirmed.toLocaleString() ?? '—'}
          subtitle="in selected period"
          icon={faCheckCircle}
          colorClass="ra-icon--green"
          loading={loading}
        />
        <SummaryCard
          title="Tokens Redeemed"
          value={summary ? `${summary.totalTokens.toLocaleString()} tokens` : '—'}
          subtitle={summary ? `avg ${summary.avgTokensPerRedemption} per redemption` : null}
          icon={faCoins}
          colorClass="ra-icon--blue"
          loading={loading}
        />
        <SummaryCard
          title="Unique Members"
          value={summary?.uniqueMembers.toLocaleString() ?? '—'}
          subtitle="distinct members redeemed"
          icon={faUsers}
          colorClass="ra-icon--purple"
          loading={loading}
        />
        <SummaryCard
          title="Pending"
          value={summary?.pending.toLocaleString() ?? '—'}
          subtitle="awaiting staff confirmation"
          icon={faClock}
          colorClass={summary?.pending > 0 ? 'ra-icon--orange' : 'ra-icon--neutral'}
          loading={loading}
        />
      </div>

      {/* Daily trend */}
      <div className="ra-section">
        <div className="ra-section__header">
          <h3>
            <FontAwesomeIcon icon={faChartBar} />
            Daily Redemption Trend
          </h3>
          <span className="ra-section__sub">Confirmed redemptions per day</span>
        </div>
        {loading
          ? <Skeleton variant="rect" height={200} />
          : <DailyTrendChart data={dailyData} loading={loading} />
        }
      </div>

      {/* Two-column: Item breakdown + Method split */}
      <div className="ra-two-col">
        {/* Item popularity */}
        <div className="ra-section">
          <div className="ra-section__header">
            <h3>
              <FontAwesomeIcon icon={faGift} />
              Item Popularity
            </h3>
            <span className="ra-section__sub">Most redeemed items</span>
          </div>
          {loading ? (
            <Skeleton variant="rect" height={160} />
          ) : itemBreakdown.length === 0 ? (
            <EmptyState message="No confirmed redemptions in this period." />
          ) : (
            <div className="ra-item-table-wrap">
              <table className="ra-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="ra-num">Redemptions</th>
                    <th className="ra-num">Tokens</th>
                    <th className="ra-num">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {itemBreakdown.map((item, i) => (
                    <tr key={item.id} className={i === 0 ? 'ra-top-row' : ''}>
                      <td className="ra-item-name">{item.name}</td>
                      <td className="ra-num">{item.count}</td>
                      <td className="ra-num">{item.tokens.toLocaleString()}</td>
                      <td className="ra-num">
                        <div className="ra-share-wrap">
                          <div className="ra-share-bar" style={{ width: `${item.pct}%` }} />
                          <span>{item.pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Method split */}
        <div className="ra-section">
          <div className="ra-section__header">
            <h3>Redemption Method</h3>
            <span className="ra-section__sub">How members are redeeming</span>
          </div>
          {loading ? (
            <Skeleton variant="rect" height={120} />
          ) : !methodSplit || summary?.total === 0 ? (
            <EmptyState message="No data yet." />
          ) : (
            <div className="ra-method-split">
              <div className="ra-method-card ra-method-card--self">
                <div className="ra-method-icon">
                  <FontAwesomeIcon icon={faMobileAlt} />
                </div>
                <div className="ra-method-body">
                  <span className="ra-method-val">{methodSplit.selfService}</span>
                  <span className="ra-method-lbl">Self-Service</span>
                  <span className="ra-method-pct">{methodSplit.selfServicePct}%</span>
                </div>
              </div>
              <div className="ra-method-card ra-method-card--staff">
                <div className="ra-method-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <div className="ra-method-body">
                  <span className="ra-method-val">{methodSplit.staffInitiated}</span>
                  <span className="ra-method-lbl">Staff-Initiated</span>
                  <span className="ra-method-pct">{methodSplit.staffPct}%</span>
                </div>
              </div>
              <div className="ra-method-split-bar">
                <div
                  className="ra-method-split-fill"
                  style={{ width: `${methodSplit.selfServicePct}%` }}
                  title={`Self-service: ${methodSplit.selfServicePct}%`}
                />
              </div>
              <p className="ra-method-note">
                {methodSplit.selfServicePct >= 70
                  ? 'Members are mostly self-serving — great for staff efficiency.'
                  : methodSplit.staffPct >= 70
                    ? 'Most redemptions are staff-initiated — consider enabling self-service for items.'
                    : 'Healthy mix of self-service and staff-initiated redemptions.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent redemptions table */}
      <div className="ra-section">
        <div className="ra-section__header">
          <h3>
            <FontAwesomeIcon icon={faHistory} />
            Recent Redemptions
          </h3>
          <span className="ra-section__sub">Latest 20 in selected period</span>
        </div>
        {loading ? (
          <Skeleton variant="rect" height={200} />
        ) : recentRows.length === 0 ? (
          <EmptyState message="No redemptions in this period." />
        ) : (
          <div className="ra-recent-table-wrap">
            <table className="ra-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Method</th>
                  <th className="ra-num">Tokens</th>
                  <th>Status</th>
                  <th>Code</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map(row => (
                  <tr key={row.id}>
                    <td className="ra-date">{formatShortDate(row.date)}</td>
                    <td className="ra-item-name">{row.itemName}</td>
                    <td>{methodLabel(row)}</td>
                    <td className="ra-num">{row.tokenCost}</td>
                    <td>{statusLabel(row.status)}</td>
                    <td className="ra-code">{row.validationCode || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedemptionAnalytics;
